import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AttendanceService, AttendanceRecord, AbsenceRecord } from '../services/attendance.service';
import { TranslateService } from '@ngx-translate/core';
import { LoginService } from '../login.service';

@Component({
    selector: 'app-attendance-tracker',
    templateUrl: './attendance-tracker.component.html',
    styleUrls: ['./attendance-tracker.component.scss'],
    standalone: false
})
export class AttendanceTrackerComponent implements OnInit {
  todayRecords: AttendanceRecord[] = [];
  isCheckedIn = false;
  isLoading = false;
  isInitialLoading = true;
  error: string | null = null;
  successMessage: string | null = null;
  currentTime: string = '';

  // Absence blocking
  absenceRecords: AbsenceRecord[] = [];
  currentAbsence: AbsenceRecord | null = null;

  // Absence modal
  showAbsenceModal = false;
  isAbsenceSaving = false;
  absenceError: string | null = null;
  absenceForm = {
    startDate: '',
    endDate: '',
    absenceType: 'vacation' as 'vacation' | 'sick',
    reason: ''
  };

  constructor(
    private attendanceService: AttendanceService,
    private router: Router,
    private translate: TranslateService,
    public ls: LoginService
  ) {
    this.updateTime();
    setInterval(() => this.updateTime(), 1000);
  }

  ngOnInit(): void {
    this.loadTodayRecords();
    this.loadTodayAbsences();
  }

  /**
   * Navigate back to appointments page
   */
  goBack(): void {
    this.router.navigate(['/appointments']);
  }

  goToReports(): void {
    this.router.navigate(['/attendance-report']);
  }

  /**
   * Update current time display
   */
  private updateTime(): void {
    const now = new Date();
    this.currentTime = now.toLocaleTimeString('he-IL', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  }

  /**
   * Load today's records
   */
  loadTodayRecords(): void {
    this.attendanceService.getTodayRecords().subscribe(
      (response: any) => {
        if (response.success) {
          this.todayRecords = response.data || [];
          this.isCheckedIn = response.isCheckedIn || false;
          this.error = null;
        }
        this.isInitialLoading = false;
      },
      (err) => {
        console.error('Error loading today records:', err);
        this.error = this.translate.instant('attendance.tracker.loadError');
        this.isInitialLoading = false;
      }
    );
  }

  /**
   * Check-in / Check-out
   */
  doCheckInOut(): void {
    // Check if today has an absence record
    if (this.isTodayAbsence()) {
      return;
    }

    this.isLoading = true;
    this.error = null;
    this.successMessage = null;

    const checkType = this.isCheckedIn ? 'check-out' : 'check-in';

    // Only try to get geolocation on mobile devices
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    if (isMobile && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          // Success - send with location
          const latitude = position.coords.latitude;
          const longitude = position.coords.longitude;
          this.performCheckInOut(checkType, latitude, longitude);
        },
        (error) => {
          // Failed to get location - send without it
          console.warn('Geolocation not available, proceeding without location:', error);
          this.performCheckInOut(checkType);
        },
        {
          enableHighAccuracy: true,
          timeout: 3000,
          maximumAge: 0
        }
      );
    } else {
      // Desktop or geolocation not supported - send without location
      this.performCheckInOut(checkType);
    }
  }

  /**
   * Perform the actual check-in/out request
   */
  private performCheckInOut(checkType: 'check-in' | 'check-out', latitude?: number, longitude?: number): void {
    this.attendanceService.checkInOut(checkType, latitude, longitude).subscribe(
      (response: any) => {
        if (response.success) {
          // Set custom success message based on check type
          this.successMessage = checkType === 'check-in' 
            ? this.translate.instant('attendance.tracker.checkInSuccess') 
            : this.translate.instant('attendance.tracker.checkOutSuccess');
          this.loadTodayRecords();
          
          // הסר את ההודעה לאחר 3 שניות
          setTimeout(() => {
            this.successMessage = null;
          }, 3000);
        }
        this.isLoading = false;
      },
      (err) => {
        console.error('Error in check-in/out:', err);
        this.error = this.translate.instant('attendance.tracker.actionError');
        this.isLoading = false;
      }
    );
  }

  /**
   * Get status badge color
   */
  getStatusColor(checkType: string): string {
    return checkType === 'check-in' ? 'green' : 'red';
  }

  /**
   * Format time for display
   */
  formatTime(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleTimeString('he-IL', {
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  /**
   * Format date for display
   */
  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('he-IL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  }

  /**
   * Open absence modal
   */
  openAbsenceModal(): void {
    this.showAbsenceModal = true;
    this.absenceError = null;
    // Reset form with today as default
    const today = new Date();
    this.absenceForm = {
      startDate: today.toISOString().split('T')[0],
      endDate: today.toISOString().split('T')[0],
      absenceType: 'vacation',
      reason: ''
    };
  }

  /**
   * Close absence modal
   */
  closeAbsenceModal(): void {
    this.showAbsenceModal = false;
    this.absenceError = null;
  }

  /**
   * Save absence record
   */
  saveAbsence(): void {
    this.isAbsenceSaving = true;
    this.absenceError = null;

    const userId = this.ls.UID;
    if (!userId) {
      this.absenceError = 'משתמש לא נמצא';
      this.isAbsenceSaving = false;
      return;
    }

    // Validate dates
    if (new Date(this.absenceForm.endDate) < new Date(this.absenceForm.startDate)) {
      this.absenceError = 'תאריך סיום חייב להיות אחרי תאריך התחלה';
      this.isAbsenceSaving = false;
      return;
    }

    this.attendanceService.addAbsence(
      userId,
      this.absenceForm.startDate,
      this.absenceForm.endDate,
      this.absenceForm.absenceType,
      this.absenceForm.reason
    ).subscribe(
      (response: any) => {
        if (response.success) {
          this.successMessage = 'רשומת היעדרות נוספה בהצלחה';
          this.closeAbsenceModal();
          // Reload absences to check if today is now an absence day
          this.loadTodayAbsences();
          // Clear success message after 3 seconds
          setTimeout(() => {
            this.successMessage = null;
          }, 3000);
        } else {
          this.absenceError = response.error || 'שגיאה בשמירת הרשומה';
        }
        this.isAbsenceSaving = false;
      },
      (err) => {
        this.isAbsenceSaving = false;
        this.absenceError = err.error?.error || 'שגיאה בשמירת הרשומה';
        console.error('Error:', err);
      }
    );
  }

  /**
   * Load today's absences
   */
  loadTodayAbsences(): void {
    if (!this.ls.UID) return;

    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth() + 1;
    
    // Create start and end dates for the current month
    const startDate = `${year}-${month.toString().padStart(2, '0')}-01`;
    const lastDay = new Date(year, month, 0).getDate();
    const endDate = `${year}-${month.toString().padStart(2, '0')}-${lastDay.toString().padStart(2, '0')}`;

    this.attendanceService.getAbsenceRecords(this.ls.UID, startDate, endDate).subscribe(
      (response: any) => {
        if (response.success && response.data) {
          this.absenceRecords = response.data;
          console.log('Loaded absences:', this.absenceRecords);
          
          // Check if today falls within any absence period
          const todayDate = this.normalizeDate(today);
          console.log('Today normalized:', todayDate);
          
          this.currentAbsence = this.absenceRecords.find(absence => {
            const startDate = this.normalizeDate(new Date(absence.StartDate));
            const endDate = this.normalizeDate(new Date(absence.EndDate));
            
            console.log('Checking absence:', {
              start: startDate,
              end: endDate,
              today: todayDate,
              inRange: todayDate >= startDate && todayDate <= endDate
            });
            
            return todayDate >= startDate && todayDate <= endDate;
          }) || null;
          
          console.log('Current absence:', this.currentAbsence);
        }
      },
      (err) => {
        console.error('Error loading absences:', err);
      }
    );
  }

  /**
   * Normalize date to midnight for comparison
   */
  private normalizeDate(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
  }

  /**
   * Check if today has an absence record
   */
  isTodayAbsence(): boolean {
    return this.currentAbsence !== null;
  }

  /**
   * Get absence tooltip text
   */
  getAbsenceTooltip(): string {
    if (!this.currentAbsence) return '';
    
    const absenceType = this.currentAbsence.AbsenceType === 'vacation' ? 'חופשה' : 'מחלה';
    const startDate = this.formatDate(this.currentAbsence.StartDate);
    const endDate = this.formatDate(this.currentAbsence.EndDate);
    
    let tooltip = `לא ניתן לדווח נוכחות\n\n`;
    tooltip += `תאריך זה מדווח כבר כהעדרות\n`;
    tooltip += `על מנת לעדכן, עלייך להיכנס לדוח החודשי ולעדכן את ההעדרות\n\n`;
    tooltip += `פרטי ההעדרות:\n`;
    tooltip += `סוג: ${absenceType}\n`;
    tooltip += `מתאריך: ${startDate}\n`;
    tooltip += `עד תאריך: ${endDate}`;
    
    if (this.currentAbsence.Reason) {
      tooltip += `\nסיבה: ${this.currentAbsence.Reason}`;
    }
    
    return tooltip;
  }
}
